/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';

import {
  ChallengeGoalTypes,
  ChallengePhaseType,
  ChallengeResultType,
  ChallengeTypeType,
  GoalType,
  LoadingStatusType,
} from 'interfaces';
import {
  CHALLENGE_PHASE,
  GOAL,
  LOADING_STATUS,
} from '../../../../../constants';
import ChallengeSection from '../../../../ChallengeSection';
import GoalsAndConditions from '../GoalsAndConditions';
import GenericGoalsSkeleton from './GenericGoalsSkeleton';
import * as S from './styles';
import { getIsDisabledDeal } from '../utils';

type ChallengeGoalsAndRewardsTypes = {
  challengeResults?: ChallengeResultType[];
  challengeStatus: LoadingStatusType;
  conditions?: any[];
  currentPhase: ChallengePhaseType | null;
  currentGroupTab: number;
  goals?: ChallengeGoalTypes[];
  goalType: GoalType;
  isGroup: boolean;
  onChangeTab: (event: any, value: number) => void;
  isParticipating: boolean | undefined;
  challengeType: ChallengeTypeType;
};

const ChallengeGoalsAndRewards = ({
  challengeResults,
  challengeStatus,
  conditions,
  currentPhase,
  currentGroupTab = 0,
  goals,
  goalType,
  isGroup,
  onChangeTab,
  isParticipating,
  challengeType,
}: ChallengeGoalsAndRewardsTypes): JSX.Element | null => {
  const { t } = useTranslation();

  /*
      The group concept is linked to 'Defis' challenges only
      A user can have several goals depending on his groups
      A single goal is displayed but a system of tabs allows you to change your choice
  */
  const hasResult = challengeResults?.[currentGroupTab]?.resultValue != null;

  const isDisabledDeal = getIsDisabledDeal(
    challengeType,
    isParticipating,
    currentPhase,
  );
  const isDisabled =
    currentPhase === CHALLENGE_PHASE.AFTER_CHALLENGE || isDisabledDeal;

  if (challengeStatus === LOADING_STATUS.PENDING) {
    return <GenericGoalsSkeleton />;
  }

  return (
    <ChallengeSection
      defaultExpanded={!hasResult}
      title={<strong>{t('challenges.detail.goals_and_rewards.title')}</strong>}
      isDisabled={isDisabled}
    >
      <S.Container>
        {isGroup && !!challengeResults?.length && goalType !== GOAL.GENERIC && (
          <S.Tabs
            isDisabled={isDisabled}
            value={currentGroupTab}
            onChange={onChangeTab}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            {challengeResults.map((result) => (
              <S.Tab key={result.id} label={result.entity.name} />
            ))}
          </S.Tabs>
        )}
        <GoalsAndConditions
          goals={goals}
          goalType={goalType}
          challengeConditions={conditions}
          challengeResults={challengeResults}
          currentTab={currentGroupTab}
          disabled={isDisabled}
        />
      </S.Container>
    </ChallengeSection>
  );
};

export default ChallengeGoalsAndRewards;
